/**
 * <oath-shoppable-videos> — S16 web component.
 *
 * Behavior (per Figma annotation 667:3393 + operator clearance C5):
 *  - Each card's <video> autoplays muted+loop+playsinline on scroll-into-view.
 *  - Whole card click toggles video.muted ↔ swaps mute icon visibility.
 *  - Arrow <a> stops propagation so PDP nav fires without flipping mute.
 *  - Mute button (top-right of video) is purely visual indicator; clicking it
 *    bubbles up to the card handler (same toggle).
 *
 * No Figma prototype motion (REST probe confirmed has_interactions=false on
 * 667:3393, 699:3170, 863:6782, 824:6540). Interactivity is implementation-
 * defined per annotation: "By default, the video will be auto-played and
 * muted. When the icon is clicked, the audio will play."
 *
 * S48 HARDENING: extends Horizon @theme/component Component + super lifecycle.
 * S82 NATIVE-ENGINE RETROFIT: the carousel is now Horizon's native
 * slideshow-component ({% render 'slideshow' %}); cards carry ref="slides[]" for
 * slideshow.js. Because the cards live under that nested component, Horizon's
 * ref system scopes them to slideshow-component — so this adapter collects them
 * via a scoped querySelector instead of this.refs.card. Per-card video/arrow
 * stay nested querySelector. Mute-toggle + offscreen-pause logic byte-identical.
 */
import { Component } from '@theme/component';

class OathShoppableVideos extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.cards = Array.from(this.querySelectorAll('.oath-shoppable-videos__card'));
    this.cards.forEach((card) => this.wireCard(card));
    this.setupAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._observer) this._observer.disconnect();
  }

  wireCard(card) {
    const video = card.querySelector('video');
    const arrow = card.querySelector('.oath-shoppable-videos__arrow');

    if (video) {
      video.muted = true;
      this.syncMuteUI(card, true);
    }

    card.addEventListener('click', (e) => {
      if (!video) return;
      if (arrow && arrow.contains(e.target)) return;
      video.muted = !video.muted;
      this.syncMuteUI(card, video.muted);
    });

    if (arrow) {
      arrow.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  syncMuteUI(card, isMuted) {
    card.classList.toggle('oath-shoppable-videos__card--muted', isMuted);
    card.classList.toggle('oath-shoppable-videos__card--unmuted', !isMuted);
    card.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
  }

  setupAutoplay() {
    const playVideo = (video) => {
      if (!video) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      this.cards.forEach((card) => playVideo(card.querySelector('video')));
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            playVideo(video);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 },
    );

    this.cards.forEach((card) => this._observer.observe(card));
  }
}

if (!customElements.get('oath-shoppable-videos')) {
  customElements.define('oath-shoppable-videos', OathShoppableVideos);
}
